//Library Imports
import { useEffect, useState } from "react";
import styled from "styled-components";
import { ReactComponent as CrossIcon } from "../../../assets/img/icons/cross.svg";
import { useSelector } from "react-redux";
import store, { RootState } from "store";
import { LoadingOutlined } from "@ant-design/icons";

//Local Imports
import UserItem from "modules/Campaigns/Shared/UserItem";
import GenericSearch from "modules/Campaigns/Shared/GenericSearch";
import { getContactsForAsset, getListForAsset, searchContactsAPI } from "modules/Campaigns/Audience/actions";
import { getQueryCampaignId, getQueryEmailId } from "modules/EmailBrowser/utils";
import RecipientCardLoader from "./RecipientCardLoader";
import InfiniteLoader from "./InfiniteLoader";
import { ASSET_AUDIENCE, VIEW_RECIPIENTS } from "constants/actionTypes";
import { Space, Spin } from "antd";
import SmallButton from "../../Content/Shared/SmallButton";
import { arrayComparator } from "utils";
import ConfirmDialog from "./ConfirmDialog";
import { useHistory } from "react-router-dom";

interface IRecipientAsider {
  hideRecipientAsider?: (loadingState: boolean) => void;
  showRecipientAsider?: boolean;
  recipientsApplyClickHandler?: (
    updatedListsState: any[],
    updatedContactsState: any[],
    updatedListObject: any[],
    updatedContactObject: any[]
  ) => void;
  sendToObjectCampaignAudience?: any[];
  setSendToObjectCampaignAudience?: (value: any[]) => void;
  selectedContactIds?: any[];
  selectedListIds?: any[];
  addInSendTo?: (value: any) => void;
}

const RecipientAsider = (props: IRecipientAsider) => {
  // Local Variables
  const {
    hideRecipientAsider,
    showRecipientAsider,
    sendToObjectCampaignAudience,
    setSendToObjectCampaignAudience,
    selectedContactIds,
    selectedListIds,
    addInSendTo,
  } = props;
  const pageLimit = 500;

  // Local States
  const [searchValue, setSearchValue] = useState("");
  const [updatedCampaignAudienceListsState, setUpdatedCampaignAudienceListsState] = useState<any[]>([]);
  const [updatedCampaignAudienceContactsState, setUpdatedCampaignAudienceContactsState] = useState<any[]>([]);
  const [initialCheckedListIds, setInitialCheckedListIds] = useState<any[]>([]);
  const [initialCheckedContactIds, setInitialCheckedContactIds] = useState<any[]>([]);
  const [checkedContactIds, setCheckedContactIds] = useState<any[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [updatedCampaignAudienceListsStateIds, setUpdatedCampaignAudienceListsStateIds] = useState<any[]>([]);
  const [updatedCampaignAudienceContactsStateIds, setUpdatedCampaignAudienceContactsStateIds] = useState<any[]>(
    []
  );
  const [updatedListObject, setUpdatedListObject] = useState<any[]>([]);
  const [updatedContactObject, setUpdatedContactObject] = useState<any[]>([]);

  // Redux States
  const campaignAudienceContactsHasMore = useSelector((state: RootState) => state.campaignAudience.hasMore);
  const campaignAudienceRecipientContactsHasMore = useSelector(
    (state: RootState) => state.campaignAudience.hasMoreSearch
  );
  const campaignAudienceLists = useSelector((state: RootState) => state.campaignAudience.data.lists);
  const campaignAudienceContacts = useSelector((state: RootState) => state.campaignAudience.data.contacts);
  const campaignAudienceRecipientContacts = useSelector(
    (state: RootState) => state.campaignAudience.data.recipientContacts
  );
  const campaignAudienceContactsLoading = useSelector(
    (state: RootState) => state.campaignAudience.contacts_loading
  );
  const contactsSearchSuccess = useSelector((state: RootState) => state.campaignAudience.success);
  const contactsSearchErrorMessage = useSelector((state: RootState) => state.campaignAudience.message);
  const paginationPageNumber: number = useSelector((state: RootState) => state.campaignAudience.pageSearch);
  const isRecipientSearch = useSelector((state: RootState) => state.viewRecipients.is_search);
  const recipientLoading: boolean = useSelector((state: RootState) => state.viewRecipients.loading);

  const history = useHistory();

  // Component Logic
  const cleanSearchField = () => {
    setSearchValue("");
    store.dispatch({
      type: VIEW_RECIPIENTS.SET_IS_SEARCH,
      payload: false,
    });
    store.dispatch({
      type: ASSET_AUDIENCE.RESET_SEARCH_CONTACTS,
    });
    store.dispatch({
      type: ASSET_AUDIENCE.ERROR_SEARCH_TERM,
      payload: { message: "", success: true },
    });
    document.getElementById("scroll-div")?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const searchContacts = (searchTerm: any) => {
    document.getElementById("scroll-div")?.scrollTo({ top: 0, behavior: "smooth" });
    setSearchValue(searchTerm);

    if (searchTerm !== searchValue) {
      store.dispatch({
        type: ASSET_AUDIENCE.CONTACTS_LOAD,
      });
      store.dispatch({
        type: ASSET_AUDIENCE.RESET_SEARCH_CONTACTS,
      });
      searchContactsAPI(getQueryCampaignId(), searchTerm, true, true, 0, pageLimit);
    }
  };

  useEffect(() => {
    return () => {
      if (isRecipientSearch) {
        cleanSearchField();
      }
    };
  }, [isRecipientSearch]);

  const getPagination = () => {
    let offset = campaignAudienceContacts.length;
    let limit = pageLimit;
    return { offset: offset, limit: limit };
  };

  const loadMore = () => {
    getContactsForAsset("", "", getQueryCampaignId(), 0, 500);
  };

  const getPaginationSearch = () => {
    let offset = campaignAudienceRecipientContacts.length;
    let limit = pageLimit;
    return { offset: offset, limit: limit };
  };

  const loadMoreSearch = () => {
    searchContactsAPI(
      getQueryCampaignId(),
      searchValue,
      true,
      true,
      getPaginationSearch().offset,
      getPaginationSearch().limit
    );
  };

  useEffect(() => {
    if (campaignAudienceLists.length > 0) {
      const checkedListIdsArray = campaignAudienceLists
        .filter((list: any) => {
          if (list.is_active) return list;
        })
        .map((list: any) => list.id);
      setInitialCheckedListIds(checkedListIdsArray);
    }
  }, [campaignAudienceLists]);

  useEffect(() => {
    if (campaignAudienceContacts.length > 0) {
      const checkedContactIdsArray = campaignAudienceContacts
        .filter((contact: any) => {
          if (contact.is_active && contact.status === "Subscribed") return contact;
        })
        .map((contact: any) => contact.id);
      setInitialCheckedContactIds(checkedContactIdsArray);
    }
  }, [campaignAudienceContacts]);

  const crossClickHandler = () => {
    // if (campaignAudienceLists.length > 0 || campaignAudienceContacts.length > 0) {
    //   const checkedListIdsArray = campaignAudienceLists
    //     .filter((list: any) => {
    //       if (list.is_active) return list;
    //     })
    //     .map((list: any) => list.id);
    //   const checkedContactIdsArray = campaignAudienceContacts
    //     .filter((contact: any) => {
    //       if (contact.is_active) return contact;
    //     })
    //     .map((contact: any) => contact.id);
    //   if (
    //     arrayComparator(initialCheckedListIds, checkedListIdsArray) &&
    //     arrayComparator(initialCheckedContactIds, checkedContactIdsArray)
    //   ) {
    //     hideRecipientAsider && hideRecipientAsider(recipientLoading);
    //   } else {
    //     if (!recipientLoading) setOpenConfirmation(true);
    //     else hideRecipientAsider && hideRecipientAsider(recipientLoading);
    //   }
    // } else {
    // }
    hideRecipientAsider && hideRecipientAsider(recipientLoading);
    if (window.location.href.includes("asiderOpen")) {
      history.push(
        `/campaigns/editionedit/publish?email_id=${getQueryEmailId()}&campaign_id=${getQueryCampaignId()}`);
    }

  };

  const closeSidebarConfirmHandler = () => {
    setOpenConfirmation(false);
    hideRecipientAsider && hideRecipientAsider(recipientLoading);
  };

  const closeSidebarCancelHandler = () => {
    setOpenConfirmation(false);
  };

  useEffect(() => {
    const checkedAudienceContacts = campaignAudienceContacts
      .filter((contact: any) => contact.is_active.toString() === "1")
      .map((contact: any) => contact.id);

    setCheckedContactIds(checkedAudienceContacts);
  }, [updatedCampaignAudienceContactsState]);

  useEffect(() => {
    const updatedCampaignAudienceListsIds = campaignAudienceLists
      .filter((list: any) => list.is_active.toString() === "1")
      .map((list: any) => list.id);

    setUpdatedCampaignAudienceListsStateIds(updatedCampaignAudienceListsIds);

    const updatedCampaignAudienceContactsIds = campaignAudienceContacts
      .filter((contact: any) => contact.is_active.toString() === "1" && contact.status === "Subscribed")
      .map((contact: any) => contact.id);

    setUpdatedCampaignAudienceContactsStateIds(updatedCampaignAudienceContactsIds);
  }, []);

  // console.log("initial list ids", initialCheckedListIds);
  // console.log("initial contact ids", initialCheckedContactIds);
  // console.log("list ids", updatedCampaignAudienceListsStateIds);
  // console.log("contact ids", updatedCampaignAudienceContactsStateIds);

  return (
    <RecipientWrapper>
      {/* apply disabled calss to disable cards while fetching recipietn count */}
      <span className="icon-style" data-qa="qa-extented-cross">
        <CrossIcon className="cross-style" onClick={crossClickHandler} />
      </span>
      <div className={`text-style d-flex justify-content-between align-items-center px-3 pt-2 ${recipientLoading ? "disabled" : ""}`}>
        <span className="ps-1">Campaign Audience</span>
        <span>
          <div className="search-wrapper">
            <GenericSearch
              inRecipientAsider={true}
              placeholder="Search people..."
              handleSearch={(searchTerm: any) => searchContacts(searchTerm)}
              handleClear={cleanSearchField}
              className="qa-search-wrapper"
            />
          </div>
        </span>
      </div>
      <div id="scroll-div" className={`wrapper scrollable ${recipientLoading ? "disabled" : ""}`}>
        {campaignAudienceLists.length > 0 &&
          campaignAudienceLists?.map((item: any, index: number) => {
            return (
              <UserItem
                hideScore={true}
                list_id={item.id.toString()}
                key={item.id}
                name={item?.name}
                listItemThumbnail1={
                  item?.topContactsThumbnail
                    ? item?.topContactsThumbnail[0]
                    : "https://media.xiq.io/static/default_campaign_img.png"
                }
                listItemThumbnail2={
                  item?.topContactsThumbnail
                    ? item?.topContactsThumbnail[1]
                    : "https://media.xiq.io/static/default_campaign_img.png"
                }
                listItemThumbnail3={
                  item?.topContactsThumbnail
                    ? item?.topContactsThumbnail[2]
                    : "https://media.xiq.io/static/default_campaign_img.png"
                }
                recipientCount={item?.totalContacts}
                recipientMenu={true}
                is_active={item?.is_active?.toString()}
                //showCheckbox={true}
                setUpdatedCampaignAudienceListsState={setUpdatedCampaignAudienceListsState}
                setUpdatedCampaignAudienceContactsState={setUpdatedCampaignAudienceContactsState}
                setUpdatedCampaignAudienceListsStateIds={setUpdatedCampaignAudienceListsStateIds}
                setUpdatedCampaignAudienceContactsStateIds={setUpdatedCampaignAudienceContactsStateIds}
                updatedContactObject={updatedContactObject}
                setUpdatedContactObject={setUpdatedContactObject}
                updatedListObject={updatedListObject}
                setUpdatedListObject={setUpdatedListObject}
                disabled={selectedListIds?.includes(item.id)}
                sendToObjectCampaignAudience={sendToObjectCampaignAudience}
                setSendToObjectCampaignAudience={setSendToObjectCampaignAudience}
                addInSendTo={addInSendTo}
              />
            );
          })}

        {isRecipientSearch ? (
          <InfiniteLoader
            dataLength={campaignAudienceRecipientContacts.length}
            next={loadMoreSearch}
            // hasMore={campaignAudienceRecipientContactsHasMore}
            hasMore={campaignAudienceRecipientContacts.length === pageLimit ? true : false}
            scrollableTarget="scroll-div"
            loader={
              campaignAudienceContactsLoading ? (
                <>
                  <RecipientCardLoader />
                  <RecipientCardLoader />
                  <RecipientCardLoader />
                </>
              ) : (
                <></>
              )
            }
          >
            {campaignAudienceRecipientContacts?.map((item: any, index: number) => (
              <UserItem
                hideScore={true}
                contact_id={item.id.toString()}
                key={item.id}
                name={item?.name}
                email={item.email}
                company={item?.organization}
                profileImage={
                  item?.thumbnail !== ""
                    ? item?.thumbnail
                    : "https://media.xiq.io/static/default_campaign_img.png"
                }
                color={item.color}
                designation={item?.designation}
                recipientMenu={true}
                is_active={
                  ["Unsubscribed", "Hard Bounce"].includes(item?.status) ? "0" : item?.is_active?.toString()
                }
                //showCheckbox={true}
                setUpdatedCampaignAudienceListsState={setUpdatedCampaignAudienceListsState}
                setUpdatedCampaignAudienceContactsState={setUpdatedCampaignAudienceContactsState}
                setUpdatedCampaignAudienceListsStateIds={setUpdatedCampaignAudienceListsStateIds}
                setUpdatedCampaignAudienceContactsStateIds={setUpdatedCampaignAudienceContactsStateIds}
                classname={["Unsubscribed", "Hard Bounce"].includes(item?.status) ? "disabled" : ""} // in case of unsubscribed it will be disabled
                status={item?.status}
                updatedContactObject={updatedContactObject}
                setUpdatedContactObject={setUpdatedContactObject}
                disabled={selectedContactIds?.includes(item.id)}
                sendToObjectCampaignAudience={sendToObjectCampaignAudience}
                setSendToObjectCampaignAudience={setSendToObjectCampaignAudience}
                addInSendTo={addInSendTo}
              />
            ))}
          </InfiniteLoader>
        ) : (
          <InfiniteLoader
            dataLength={campaignAudienceContacts.length}
            next={loadMore}
            // hasMore={campaignAudienceContactsHasMore}
            hasMore={campaignAudienceContacts.length === pageLimit ? true : false}
            scrollableTarget="scroll-div"
            loader={
              campaignAudienceContactsLoading ? (
                <>
                  <RecipientCardLoader />
                  <RecipientCardLoader />
                  <RecipientCardLoader />
                </>
              ) : (
                <></>
              )
            }
          >
            {campaignAudienceContacts?.map((item: any, index: number) => {
              return (
                <UserItem
                  hideScore={true}
                  contact_id={item.id.toString()}
                  key={item.id}
                  name={item?.name}
                  email={item.email}
                  company={item?.organization}
                  profileImage={
                    item?.thumbnail !== ""
                      ? item?.thumbnail
                      : "https://media.xiq.io/static/default_campaign_img.png"
                  }
                  color={item.color}
                  designation={item?.designation}
                  recipientMenu={true}
                  is_active={
                    ["Unsubscribed", "Hard Bounce"].includes(item?.status) ? "0" : item?.is_active?.toString()
                  }
                  //showCheckbox={true}
                  setUpdatedCampaignAudienceListsState={setUpdatedCampaignAudienceListsState}
                  setUpdatedCampaignAudienceContactsState={setUpdatedCampaignAudienceContactsState}
                  setUpdatedCampaignAudienceListsStateIds={setUpdatedCampaignAudienceListsStateIds}
                  setUpdatedCampaignAudienceContactsStateIds={setUpdatedCampaignAudienceContactsStateIds}
                  classname={["Unsubscribed", "Hard Bounce"].includes(item?.status) ? "disabled" : ""} // in case of unsubscribed it will be disabled
                  status={item?.status}
                  updatedContactObject={updatedContactObject}
                  setUpdatedContactObject={setUpdatedContactObject}
                  disabled={selectedContactIds?.includes(item.id)}
                  sendToObjectCampaignAudience={sendToObjectCampaignAudience}
                  setSendToObjectCampaignAudience={setSendToObjectCampaignAudience}
                  addInSendTo={addInSendTo}
                />
              );
            })}
          </InfiniteLoader>
        )}

        {campaignAudienceContactsLoading && !isRecipientSearch ? (
          <>
            <RecipientCardLoader />
            <RecipientCardLoader />
            <RecipientCardLoader />
          </>
        ) : isRecipientSearch && campaignAudienceContactsLoading ? (
          <>
            <RecipientCardLoader />
            <RecipientCardLoader />
            <RecipientCardLoader />
          </>
        ) : isRecipientSearch &&
          campaignAudienceRecipientContacts.length === 0 &&
          !campaignAudienceContactsLoading ? (
          contactsSearchSuccess ? (
            "No recipient(s) found"
          ) : (
            contactsSearchErrorMessage
          )
        ) : campaignAudienceContacts.length === 0 &&
          campaignAudienceLists.length === 0 &&
          !campaignAudienceContactsLoading ? (
          "No recipient(s) found"
        ) : (
          <></>
        )}
        {openConfirmation && (
          <ConfirmDialog
            confirm="Yes"
            cancel="Cancel"
            onConfirm={closeSidebarConfirmHandler}
            onCancel={closeSidebarCancelHandler}
            title={"All your changes will be lost."}
            titleQuestion="Are you sure you want to close the sidebar?"
          />
        )}
      </div>
      {recipientLoading && (
        <div className="spin-loader-div">
          {/* <Spin size="large" /> */}
          {recipientLoading ? <Spin size="large" /> : null}
        </div>
      )}

      {/* <div className={`bottom-floating-action publish-footer ${recipientLoading ? "disabled" : ""}`}>
      / <Space align="end">
          {/* {recipientLoading ? <Spin /> : null} */}
      {/* <SmallButton
            disabled={
              (arrayComparator(initialCheckedListIds, updatedCampaignAudienceListsStateIds) &&
                arrayComparator(initialCheckedContactIds, updatedCampaignAudienceContactsStateIds)) ||
              campaignAudienceContactsLoading ||
              (campaignAudienceLists.length === 0 && campaignAudienceContacts.length === 0) ||
              (campaignAudienceLists.length === 0 &&
                searchValue.length > 0 &&
                campaignAudienceRecipientContacts.length === 0)
            }
            onClick={() =>
              props.recipientsApplyClickHandler &&
              props.recipientsApplyClickHandler(
                updatedCampaignAudienceListsState,
                updatedCampaignAudienceContactsState,
                updatedListObject,
                updatedContactObject
              )
            }
            title={"Apply"}
          />}
        </Space>
      </div> */}
    </RecipientWrapper>
  );
};

export default RecipientAsider;

const RecipientWrapper = styled.div`
  transition: 0.5s;
  width: 470px;
  background-color: #f7f7f7;
  position: relative;
  height: 100vh;
  padding-top: 100px;
  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  .spin-loader-div {
    position: absolute;
    top: 50%;
    width: 100%;
    display: flex;
    justify-content: center;
  }
  .search-wrapper {
    width: 175px;
    .search-input {
      width: 100px !important;
      margin-top: 2px !important;
    }
    span.clear-search {
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: 6px !important;
      }
    }
  }
  .wrapper {
    padding: 20px;
  }
  .text-style {
    color: #7e7e7e;
    font-size: 18px;
    padding-bottom: 10px;
    font-family: "Roboto Slab";
    font-weight: 400;
  }
  .icon-style {
    display: flex;
    justify-content: flex-end;
    padding: 20px 20px 5px 0px;
    margin-top: 15px;
  }
  .cross-style {
    cursor: pointer;
    width: 25px;
    height: 25px;
  }
  .scrollable {
    max-height: calc(100vh - 294px);
    overflow: auto;
    scrollbar-width: thin;
    scrollbar-color: #001760 transparent;
    padding-top: 10px;
    padding-bottom: 0;
    height: calc(100vh - 290px) !important;
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      border-radius: 5px;
    }
    /* Track */
    &::-webkit-scrollbar-track {
      background: #fff;
      margin-top: 10px;
      margin-bottom: 15px;
    }
    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #001760;
    }
    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }
  .bottom-floating-action {
    padding-top: 20px;
    padding-bottom: 20px;
    padding-right: 20px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    background-color: #fff;
    min-height: 80px;
    min-width: 468px;
    margin-left: 1px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      padding-top: 28px;
      padding-bottom: 10px;
    }
  }
`;
